import { AutofillHome } from "components/AutoFill/AutofillHome";
import { DashboardLayout } from "components/DashboardLayout";
import { RegisterAgent } from "components/RegisteredAgent/RegisterAgent";
import { TaskList } from "components/Task/TaskList";
import BillingTabBar from "components/billing/BillingTabBar";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { Mailbox } from "components/mailbox/mailbox";
import { TaxAndCompliance } from "components/taxCompliance/TaxAndCompliance";
import AddTeamMember from "components/teamMembers/addTeamMember";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { usePageBreadcrumb } from "hooks/usePageBreadcrumb";
import { usePageTitle } from "hooks/usePageTitle";
import { useDisableContentTopPadding } from "hooks/useDisableContentTopPadding";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { Invoices } from "pages/TPPayments/Invoices";
import { ReactNode, useEffect, useState } from "react";
import {
  Link,
  NavLink,
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { createPortal } from "react-dom";
import { useSecondaryNav } from "components/AppLayout/SecondaryNavContext";
import { useBreadcrumbLeadingSlot } from "components/AppLayout/BreadcrumbLeadingSlotContext";
import { CrmMobilePageNav } from "components/crm/CrmMobilePageNav";
import { usePageTitleSetter } from "hooks/usePageTitleSetter";
import { useGetGroupDataQuery } from "store/apis/teamSetting";
import { Group } from "types/Models/group";
import "../../static/styles/components/groupData.css";
import Documents from "../documents/documents";
import { RecycleBin } from "../documents/RecycleBin/RecycleBin";
import { CrmPerks } from "./CrmPerks";
import { CrmQuotes } from "./CrmQuotes";
import { Messages } from "./messages";
import { InternalTeam } from "components/teamMembers/InternalTeam";
import { Header } from "components/DesignSystem/Header/Header";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import { Ticket } from "components/Ticket/Tickets";
import { AuditLogs } from "pages/AuditLogs/AuditLogs";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Registrations } from "pages/Registrations/Registrations";
import { StateRegistrationDetail } from "pages/Registrations/StateRegistrationDetail";
import { SalesNexusTracker } from "pages/SalesTaxDashboard/SalesNexusTracker";
import { AccountInfoTabView } from "components/CompanyAccountInfo/AccountInfoTabView";
import { TodoList } from "pages/TodoList/TodoList";
import { AdvanceTaxAndProfitsDashboard } from "components/AdvanceTaxAndProfits/AdvanceTaxAndProfitsDashboard";
import { StateIncomeTax } from "components/StateIncomeTax/StateIncomeTax";
import { GroupNote } from "components/BooksCrm/GroupNote";
import { OldTaskList } from "components/Task/OldTaskList";
import { DeadlineAlarm } from "components/DeadlineAlarm/DeadlineAlarm";
import { EditGroupName } from "components/Entity/EditGroupName";
import { useModal } from "hooks/useModal";
import { BrandOverview } from "v2/components/brandOverview/BrandOverview";
import { ArrowLeft } from "components/icons/ArrowLeft";
import EntityDetail from "components/Entity/EntityDetail";
import { GroupHomePage } from "components/GroupHome/GroupHomePage";
import { CompleteWithAI } from "v2/pages/CompleteWithAI/CompleteWithAI";
import { HeaderActions } from "components/AppLayout/HeaderActions";
import { GroupAiToggle } from "components/crm/GroupAiToggle";
import { DocumentsRootRedirect } from "components/crm/DocumentsRootRedirect";
import { ChurnDetailsModal } from "components/serviceTeam/ChurnDetailsModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCrmTabs } from "hooks/useCrmTabs";
import { getCrmTabState } from "util/getCrmTabState";
import { CRM_PAGE } from "constants/crmPages";

const CrmTabList = ({ data }: { data: Group | null }) => {
  const crmTabs = useCrmTabs(data);

  return (
    <>
      {crmTabs.map(
        ({ url, name, exact, omitUrl, activeBasePath, extraActivePaths }) => (
          <CrmTab
            tabUrl={url}
            exact={exact}
            omitUrl={omitUrl}
            activeBasePath={activeBasePath}
            extraActivePaths={extraActivePaths}
            key={url}
          >
            {name}
          </CrmTab>
        )
      )}
    </>
  );
};

const CrmTab = ({
  tabUrl = "",
  children,
  exact,
  target,
  omitUrl,
  activeBasePath,
  extraActivePaths,
}: {
  tabUrl: string;
  children: ReactNode;
  exact: boolean;
  target?: string;
  omitUrl: boolean;
  activeBasePath?: string;
  extraActivePaths?: string[];
}) => {
  const { url } = useRouteMatch();
  const { pathname } = useLocation();
  const setPageTitle = usePageTitleSetter();
  const { link } = useConstructInternalLink();

  const { to, toPath, isSelected } = getCrmTabState({
    tab: { url: tabUrl, exact, omitUrl, activeBasePath, extraActivePaths },
    url,
    pathname,
    link,
  });

  const matchesExtraActivePath = (currentPath: string) =>
    Boolean(extraActivePaths?.some((p) => currentPath.includes(`${url}${p}`)));

  const isActive =
    activeBasePath || extraActivePaths
      ? (_: any, location: any) => {
          const base = activeBasePath
            ? location.pathname.startsWith(`${url}${activeBasePath}`)
            : exact
            ? location.pathname === toPath
            : location.pathname.startsWith(toPath);
          return base || matchesExtraActivePath(location.pathname);
        }
      : undefined;

  useEffect(() => {
    if (isSelected && !omitUrl && typeof children === "string") {
      setPageTitle(children);
    }
  }, [isSelected, omitUrl, children, setPageTitle]);

  return (
    <NavLink
      to={to}
      exact={Boolean(exact)}
      target={target}
      isActive={isActive}
      className="t-relative t-my-0.5 t-flex t-items-center t-rounded-md t-px-3 t-py-2 t-text-body-sm t-capitalize t-text-text-60 t-no-underline t-transition-all hover:t-bg-neutral-0"
      activeClassName="crm-tab-active t-bg-neutral-0 !t-text-text-100 t-font-medium"
    >
      <span className="t-absolute t-left-0 t-top-1/2 t-hidden t-h-4 t-w-0.5 -t-translate-y-1/2 t-rounded-r t-bg-purple-70 [.crm-tab-active_&]:t-block" />
      {children}
    </NavLink>
  );
};

export const GroupData = () => {
  const { pathname } = useLocation();
  const { groupId } = useParams<{ groupId: string }>();
  const { path, url } = useRouteMatch();
  const [clickedNote, setClickedNote] = useState<Group | null>();
  const { isAdmin, isForeignCA } = useRoleBasedView();
  const isChatPath = pathname?.includes("/chat");
  const { dispatch } = useCurrentGroupContext();

  useDisableContentTopPadding(isChatPath);
  let { data, isLoading, isSuccess } = useGetGroupDataQuery(
    { groupId },
    { skip: !groupId }
  );
  const editGroupName = useModal();
  const { registerActive, headerSlot, bodySlot } = useSecondaryNav();
  const { slot: breadcrumbLeadingSlot } = useBreadcrumbLeadingSlot();

  const query = useQuery();
  const { updateMultiple } = useUpdateQuery();
  const completeChurnDraftId = query.get("draft_id");
  const isCompleteChurnOpen =
    query.get("complete_churn") === "true" && Boolean(completeChurnDraftId);
  const closeCompleteChurnModal = () =>
    updateMultiple([
      { query: "complete_churn", value: null },
      { query: "draft_id", value: null },
    ]);

  useEffect(() => registerActive(), [registerActive]);

  usePageTitle(`${data?.name || "Group"}`);

  usePageBreadcrumb(isAdmin ? data?.name : null, url);

  useEffect(() => {
    setClickedNote(data);

    return () => setClickedNote(null);
  }, [data]);

  useEffect(() => {
    if (data) {
      dispatch({ type: "SET_GROUP", payload: data });
    }
  }, [groupId, data?.uuid, data?.name]);

  const isEmpty = !data;

  return (
    <Async.Root isLoading={isLoading} isSuccess={isSuccess} isEmpty={isEmpty}>
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        <DashboardContainer className="groupCRMPage t-h-full">
          {/* <DashboardContainer.Header>
            <div className="backBar t-shrink-0 t-flex-grow-0 t-basis-auto">
              <div className="leftHeadBack">
                <i
                  role="article"
                  className="fas fa-arrow-left"
                  onClick={closeWindow}
                  aria-hidden={true}
                ></i>
                <div className="t-flex t-gap-1.5 t-items-center">
                  <span className="t-mr-2">{data?.name}</span>
                  {isAdmin && (
                    <Button size="xsmall" onClick={editGroupName.open}>
                      Edit
                    </Button>
                  )}
                  <GroupNote
                    groupId={clickedNote?.uuid ?? ""}
                    countVisibility={false}
                    allNotesCount={data?.notes_count ?? 0}
                  />
                </div>
              </div>
            </div>
          </DashboardContainer.Header> */}
          <DashboardContainer.Content>
            {isAdmin &&
              breadcrumbLeadingSlot &&
              createPortal(
                <GroupNote
                  groupId={clickedNote?.uuid ?? ""}
                  countVisibility={false}
                  allNotesCount={data?.notes_count ?? 0}
                  className="t-min-w-6"
                />,
                breadcrumbLeadingSlot
              )}
            {headerSlot &&
              createPortal(
                <div className="t-flex t-items-center t-gap-2">
                  <Link
                    to="/admin/crm"
                    className="all:unset t-flex t-cursor-pointer t-items-center t-text-text-30"
                  >
                    <ArrowLeft className="t-size-4" />
                  </Link>
                  <span className="t-text-body t-font-medium t-text-neutral-40">
                    Ops CRM
                  </span>
                </div>,
                headerSlot
              )}
            {isAdmin && (
              <HeaderActions>
                <GroupAiToggle groupId={groupId} />
              </HeaderActions>
            )}
            {isAdmin && (
              <div className="t-mb-4 md:t-hidden">
                <CrmMobilePageNav data={data!} />
              </div>
            )}
            {bodySlot &&
              createPortal(
                <div className="t-flex t-flex-col t-gap-0.5">
                  <CrmTabList data={data!} />
                </div>,
                bodySlot
              )}
            <Switch>
              <Route path={`${path}/perks`}>
                <CrmPerks />
              </Route>

              <Route path={`${path}/filings`}>
                <OldTaskList />
              </Route>

              <Route path={`${path}/new-filings`}>
                <TaskList />
              </Route>

              <Route path={`${path}/accountinfo`}>
                <AccountInfoTabView group={data} />
              </Route>
              <Route exact path={`${path}/team-settings`}>
                <AddTeamMember />
              </Route>
              <Route exact path={`${path}/internal-team-settings`}>
                <InternalTeam />
              </Route>
              <Route exact path={`${path}/company-tickets`}>
                <Ticket companyGroupUUID={data?.uuid} />
              </Route>
              <Route path={`${path}/billing`}>
                <BillingTabBar crmId={data?.uuid} />
              </Route>
              <Route path={`${path}/autofills`}>
                <DashboardLayout>
                  <AutofillHome />
                </DashboardLayout>
              </Route>
              <Route
                exact
                path={[
                  `${path}/chat`,
                  `${path}/chat/:channelId/`,
                  `${path}/chat/:channelId/:messageId`,
                ]}
              >
                <Messages />
              </Route>

              <Route path={`${path}/entity/:entityId`}>
                <EntityDetail parentUrl={url} />
              </Route>
              <Route exact path={path}>
                <div className="t-px-6">
                  <BrandOverview />
                </div>
              </Route>
              <Route exact path={`${path}/home`}>
                <GroupHomePage groupId={groupId} />
              </Route>
              <Route exact path={`${path}/complete-with-ai`}>
                <CompleteWithAI />
              </Route>
              <Route exact path={`${path}/todolist`}>
                <TodoList />
              </Route>
              <Route path={`${path}/advance-tax`}>
                <AdvanceTaxAndProfitsDashboard />
              </Route>
              <Route path={`${path}/state-income-tax`}>
                <StateIncomeTax />
              </Route>
              <Route exact path={`${path}/registeredagent`}>
                <RegisterAgent />
              </Route>
              <Route exact path={`${path}/mail/:mailId`}>
                <Redirect
                  to={
                    isForeignCA
                      ? `/practice/crm/${groupId}/mail`
                      : `/admin/crm/${groupId}/mail`
                  }
                />
              </Route>
              <Route path={`${path}/mail`}>
                <DashboardLayout
                  header={
                    <Header
                      v2
                      title="Inbox"
                      right={
                        <div>
                          <EntitySelector size="small" />
                        </div>
                      }
                    />
                  }
                >
                  <Mailbox
                    userGroupId={data?.uuid}
                    hasMailroomSubscription={data?.has_mailroom_subscription}
                  />
                </DashboardLayout>
              </Route>
              <Route exact path={`${path}${CRM_PAGE.DOCUMENTS.url}`}>
                <DocumentsRootRedirect />
              </Route>
              <Route exact path={`${path}/documents/recyclebin`}>
                <RecycleBin appUrl={url} />
              </Route>
              <Route path={`${path}/documents/:docId`}>
                <Documents appUrl={url} />
              </Route>
              <Route path={`${path}/deadlines`}>
                <DeadlineAlarm />
              </Route>

              <Route exact path={`${path}/state-compliance`}>
                <Registrations />
              </Route>
              <Route exact path={`${path}/state-compliance/:stateId`}>
                <StateRegistrationDetail />
              </Route>

              <Route exact path={`${path}/tax`}>
                <TaxAndCompliance />
              </Route>

              {!isForeignCA && (
                <Route path={`${path}/quotes`}>
                  <CrmQuotes />
                </Route>
              )}

              <Route path={`${path}/auditlog`}>
                <AuditLogs />
              </Route>

              <Route path={`${path}/transfers`}>
                <Invoices />
              </Route>
              <Route path={`${path}/sales-tax`}>
                <SalesNexusTracker />
              </Route>
            </Switch>{" "}
            <EditGroupName
              {...editGroupName}
              groupId={groupId}
              groupName={data?.name || ""}
            />
            <ChurnDetailsModal
              isOpen={isCompleteChurnOpen}
              close={closeCompleteChurnModal}
              groupId={groupId}
              draftId={completeChurnDraftId ?? undefined}
            />
          </DashboardContainer.Content>
        </DashboardContainer>
      </Async.Success>
    </Async.Root>
  );
};
